import {
    IonAvatar,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSearchbar,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    americanFootballOutline,
    peopleOutline,
    personOutline,
    refreshOutline,
    searchOutline,
    shieldOutline
} from "ionicons/icons";
import {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import { useHistory } from "react-router-dom";
import {
    Competition,
    Player,
    Team,
    getCompetitions,
    getPlayersList,
    getTeams
} from "../services/mexfbaApi";
import "./Search.css";

type SearchFilter =
    | "Todos"
    | "Equipos"
    | "Jugadores"
    | "Competencias";

interface SearchResult {
    id: string;
    name: string;
    type: Exclude<SearchFilter, "Todos">;
    image?: string;
    subtitle: string;
    color?: string;
}

const SEARCH_FILTERS: SearchFilter[] = [
    "Todos",
    "Equipos",
    "Jugadores",
    "Competencias"
];

function getPlayerPosition(position?: unknown): string {
    const positions: Record<string, string> = {
        "1": "QB",
        "2": "RB",
        "3": "WR",
        "4": "DL",
        "5": "LB",
        "6": "DB",
        "7": "K",
        "8": "OL"
    };

    const normalizedPosition = String(
        position ?? ""
    )
        .trim()
        .toUpperCase();

    /*
        Si ya llega QB, RB, WR, etc., se conserva.
    */
    if (
        [
            "QB",
            "RB",
            "WR",
            "DL",
            "LB",
            "DB",
            "K",
            "OL"
        ].includes(normalizedPosition)
    ) {
        return normalizedPosition;
    }

    return positions[normalizedPosition] || "Jugador";
}

function getAssetUrl(image?: unknown): string {
    const normalizedImage =
        typeof image === "string"
            ? image.trim()
            : "";

    if (!normalizedImage) {
        return "";
    }

    if (
        normalizedImage.startsWith("http://") ||
        normalizedImage.startsWith("https://") ||
        normalizedImage.startsWith("data:")
    ) {
        return normalizedImage;
    }

    return `${process.env.PUBLIC_URL || ""}/${normalizedImage.replace(
        /^(\.\.\/)+/,
        ""
    )}`;
}

function getInitial(name: string): string {
    return name.trim().charAt(0).toUpperCase() || "?";
}

/*
    Los datos históricos pueden tener campos faltantes, null
    o estructuras antiguas. Nunca se debe llamar toLowerCase()
    directamente sobre valores provenientes de JSON.
*/
function normalizeSearchText(value: unknown): string {
    return String(value ?? "")
        .trim()
        .toLocaleLowerCase("es-MX");
}

function getEntityName(entity: any): string {
    if (!entity || typeof entity !== "object") {
        return "";
    }

    return String(
        entity.nombre ??
        entity.name ??
        entity.Info?.nombre ??
        entity.Info?.name ??
        ""
    ).trim();
}

function getEntityId(entity: any): string {
    if (!entity || typeof entity !== "object") {
        return "";
    }

    return String(
        entity.id ??
        entity.Info?.id ??
        ""
    ).trim();
}

function matchesSearchQuery(
    entity: any,
    normalizedQuery: string
): boolean {
    if (!normalizedQuery) {
        return false;
    }

    return normalizeSearchText(
        getEntityName(entity)
    ).includes(normalizedQuery);
}

const Search: React.FC = () => {
    const history = useHistory();

    const [teams, setTeams] = useState<Team[]>([]);
    const [players, setPlayers] = useState<Player[]>([]);
    const [competitions, setCompetitions] = useState<Competition[]>(
        []
    );

    const [searchQuery, setSearchQuery] = useState("");
    const [activeFilter, setActiveFilter] =
        useState<SearchFilter>("Todos");

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadSearchData = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const [teamsData, playersData, competitionsData] =
                    await Promise.all([
                        getTeams(forceRefresh),
                        getPlayersList(forceRefresh),
                        getCompetitions(forceRefresh)
                    ]);

                setTeams(teamsData);
                setPlayers(playersData);
                setCompetitions(competitionsData);
            } catch (requestError) {
                console.error(
                    "No fue posible cargar el buscador:",
                    requestError
                );

                setError(
                    "No fue posible cargar los equipos, jugadores y competencias."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        []
    );

    useEffect(
        function () {
            loadSearchData();
        },
        [loadSearchData]
    );

const results = useMemo(
    function () {
        const normalizedQuery = normalizeSearchText(
            searchQuery
        );

        if (!normalizedQuery) {
            return [];
        }

        const safeTeams = Array.isArray(teams)
            ? teams
            : [];

        const safePlayers = Array.isArray(players)
            ? players
            : [];

        const safeCompetitions = Array.isArray(competitions)
            ? competitions
            : [];

        const teamResults: SearchResult[] = safeTeams
            .filter(function (team) {
                return matchesSearchQuery(
                    team,
                    normalizedQuery
                );
            })
            .map(function (team) {
                const name = getEntityName(team);

                return {
                    id: getEntityId(team),
                    name: name || "Equipo sin nombre",
                    type: "Equipos" as const,

                    image: getAssetUrl(
                        (team as any)?.logo
                    ),
                    subtitle: "Equipo",
                    color: String(
                        (team as any)?.color ?? ""
                    )
                };
            })
            .filter(function (team) {
                return team.id !== "";
            });

        const playerResults: SearchResult[] = safePlayers
            .filter(function (player) {
                return matchesSearchQuery(
                    player,
                    normalizedQuery
                );
            })
            .map(function (player) {
                const playerData = player as any;

                const playerTeamId = String(
                    playerData?.equipo_id ??
                    playerData?.teamId ??
                    ""
                );

                const team = safeTeams.find(
                    function (currentTeam) {
                        return (
                            getEntityId(currentTeam) ===
                            playerTeamId
                        );
                    }
                );

                const name = getEntityName(player);

                return {
                    id: getEntityId(player),
                    name: name || "Jugador sin nombre",
                    type: "Jugadores" as const,
                    image: getAssetUrl(
                        playerData?.img ??
                        playerData?.image ??
                        playerData?.foto
                    ),
                    subtitle: `${getPlayerPosition(
                        String(
                            playerData?.posicion ??
                            playerData?.pos ??
                            playerData?.positionId ??
                            ""
                        )
                    )} · ${
                        getEntityName(team) ||
                        "Equipo por confirmar"
                    }`
                };
            })
            .filter(function (player) {
                return player.id !== "";
            });

        const competitionResults: SearchResult[] =
            safeCompetitions
                .filter(function (competition) {
                    return matchesSearchQuery(
                        competition,
                        normalizedQuery
                    );
                })
                .map(function (competition) {
                    const competitionData =
                        competition as any;

                    const name = getEntityName(
                        competition
                    );

                    return {
                        id: getEntityId(competition),
                        name:
                            name ||
                            "Competencia sin nombre",
                        type: "Competencias" as const,
                        image: getAssetUrl(
                            competitionData?.logo ??
                            competitionData?.Info?.logo
                        ),
                        subtitle: "Competencia"
                    };
                })
                .filter(function (competition) {
                    return competition.id !== "";
                });

        const allResults = [
            ...teamResults,
            ...playerResults,
            ...competitionResults
        ];

        if (activeFilter === "Todos") {
            return allResults;
        }

        return allResults.filter(function (result) {
            return result.type === activeFilter;
        });
    },
    [
        activeFilter,
        competitions,
        players,
        searchQuery,
        teams
    ]
);


    function getResultIcon(result: SearchResult) {
        if (result.type === "Equipos") {
            return shieldOutline;
        }

        if (result.type === "Jugadores") {
            return personOutline;
        }

        return americanFootballOutline;
    }

    function openResult(result: SearchResult) {
        if (result.type === "Equipos") {
            history.push(`/team/${result.id}`);
            return;
        }

        if (result.type === "Jugadores") {
            history.push(`/player/${result.id}`);
            return;
        }

        history.push(`/results/${result.id}`);
    }

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadSearchData(true);
        event.detail.complete();
    }

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="search-toolbar">
                    <IonTitle>Buscar</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar búsqueda"
                            disabled={refreshing}
                            onClick={function () {
                                loadSearchData(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="search-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando buscador..."
                    />
                </IonRefresher>

                <section className="search-hero">
                    <IonIcon icon={searchOutline} />

                    <div>
                        <span>MEXFBA</span>
                        <h1>Encuentra lo que buscas</h1>
                        <p>
                            Equipos, jugadores y competencias en un solo lugar.
                        </p>
                    </div>
                </section>

                <main className="search-page">
                    <IonSearchbar
                        className="search-input"
                        value={searchQuery}
                        debounce={220}
                        placeholder="Buscar equipo, jugador o competencia"
                        onIonChange={function (event) {
                            setSearchQuery(event.detail.value || "");
                        }}
                    />

                    <div className="search-filters">
                        {SEARCH_FILTERS.map(function (filter) {
                            return (
                                <IonButton
                                    key={filter}
                                    fill={
                                        activeFilter === filter
                                            ? "solid"
                                            : "outline"
                                    }
                                    onClick={function () {
                                        setActiveFilter(filter);
                                    }}
                                >
                                    {filter}
                                </IonButton>
                            );
                        })}
                    </div>

                    {loading ? (
                        <IonProgressBar type="indeterminate" />
                    ) : error ? (
                        <section className="search-empty">
                            <IonIcon icon={searchOutline} />
                            <strong>No fue posible abrir el buscador</strong>
                            <span>{error}</span>

                            <IonButton
                                onClick={function () {
                                    loadSearchData(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </section>
                    ) : !searchQuery.trim() ? (
                        <section className="search-empty">
                            <IonIcon icon={peopleOutline} />
                            <strong>Comienza una búsqueda</strong>
                            <span>
                                Escribe el nombre de un jugador, equipo o
                                competencia.
                            </span>
                        </section>
                    ) : results.length === 0 ? (
                        <section className="search-empty">
                            <IonIcon icon={searchOutline} />
                            <strong>Sin resultados</strong>
                            <span>
                                No encontramos coincidencias para “
                                {searchQuery.trim()}”.
                            </span>
                        </section>
                    ) : (
                        <>
                            <div className="search-results-heading">
                                <div>
                                    <span>RESULTADOS</span>
                                    <h2>
                                        {results.length}{" "}
                                        {results.length === 1
                                            ? "coincidencia"
                                            : "coincidencias"}
                                    </h2>
                                </div>
                            </div>

                            <div className="search-results-list">
                                {results.map(function (result) {
                                    return (
                                        <button
                                            className="search-result"
                                            key={`${result.type}-${result.id}`}
                                            type="button"
                                            onClick={function () {
                                                openResult(result);
                                            }}
                                        >
                                            <IonAvatar
                                                style={
                                                    result.color
                                                        ? {
                                                              background:
                                                                  result.color
                                                          }
                                                        : undefined
                                                }
                                            >
                                                {result.image ? (
                                                    <img
                                                        src={result.image}
                                                        alt={
                                                            result.type ===
                                                            "Jugadores"
                                                                ? `Foto de ${result.name}`
                                                                : `Logo de ${result.name}`
                                                        }
                                                    />
                                                ) : (
                                                    <span>
                                                        {result.type ===
                                                        "Jugadores"
                                                            ? getInitial(
                                                                  result.name
                                                              )
                                                            : (
                                                                  <IonIcon
                                                                      icon={getResultIcon(
                                                                          result
                                                                      )}
                                                                  />
                                                              )}
                                                    </span>
                                                )}
                                            </IonAvatar>

                                            <div className="search-result__content">
                                                <strong>
                                                    {result.name}
                                                </strong>
                                                <span>
                                                    {result.subtitle}
                                                </span>
                                            </div>

                                            <span className="search-result__type">
                                                {result.type === "Equipos"
                                                    ? "Equipo"
                                                    : result.type ===
                                                      "Jugadores"
                                                    ? "Jugador"
                                                    : "Liga"}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </main>
            </IonContent>
        </IonPage>
    );
};

export default Search;
